import { useEffect, useMemo, useState } from 'react';
import TextBlock from 'components/formComponents/Base/TextBlock';
import InputField from 'components/formComponents/Base/InputField';
import CtaButtonWithIcon from 'components/formComponents/Base/CtaButtonWithIcon';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useFormContext } from 'react-hook-form';
import { useSubmitExistingAutomatedBankruptcyContactMutation } from '../../../partyApplicationApi';
import UnionRoleSelect from '../UnionSection/UnionRole';
import { UnionRole } from '../../../../../types/PartyApplication/partyApplication';
import { SubmitSectionTexts } from '../../../../../types/PartyApplication/PartyApplicationTexts';

type Props = {
  incidentId: string;
  texts?: SubmitSectionTexts;
  onSubmitted: () => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
};

const ExistingBankruptcyContactBlock = ({
  incidentId,
  texts,
  onSubmitted,
  onSubmittingChange,
}: Props) => {
  const [submitContact, { isLoading }] =
    useSubmitExistingAutomatedBankruptcyContactMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { getValues, setError, clearErrors, setValue } = useFormContext();

  useEffect(() => {
    const currentUnionRole = Number(getValues('unionRole') ?? 0);
    if (
      currentUnionRole !== UnionRole.UnionRepresentative &&
      currentUnionRole !== UnionRole.UnionLocalRepresentative
    ) {
      setValue('unionRole', UnionRole.UnionRepresentative, {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, [getValues, setValue]);

  const emailRegex = useMemo(() => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  }, []);

  const validateRequiredField = (
    fieldName:
      | 'companyContactFirstname'
      | 'companyContactLastname'
      | 'companyContactEmail'
      | 'companyContactPhone',
    value: string,
    message: string
  ): boolean => {
    if (value.trim().length > 0) {
      clearErrors(fieldName);
      return true;
    }

    setError(fieldName, {
      type: 'manual',
      message,
    });
    return false;
  };

  const handleSubmitContact = async () => {
    setErrorMessage(null);

    const firstName = String(getValues('companyContactFirstname') ?? '');
    const lastName = String(getValues('companyContactLastname') ?? '');
    const email = String(getValues('companyContactEmail') ?? '');
    const phone = String(getValues('companyContactPhone') ?? '');
    const unionRole = Number(getValues('unionRole') ?? 0);

    const hasFirstName = validateRequiredField(
      'companyContactFirstname',
      firstName,
      'Ange förnamn'
    );
    const hasLastName = validateRequiredField(
      'companyContactLastname',
      lastName,
      'Ange efternamn'
    );
    const hasEmail = validateRequiredField(
      'companyContactEmail',
      email,
      'Ange e-post'
    );
    const hasPhone = validateRequiredField(
      'companyContactPhone',
      phone,
      'Ange telefonnummer'
    );

    if (hasEmail && !emailRegex.test(email.trim())) {
      setError('companyContactEmail', {
        type: 'manual',
        message: 'Ange en giltig e-postadress',
      });
      return;
    }

    if (!hasFirstName || !hasLastName || !hasEmail || !hasPhone) {
      return;
    }

    if (
      unionRole !== UnionRole.UnionRepresentative &&
      unionRole !== UnionRole.UnionLocalRepresentative
    ) {
      setError('unionRole', {
        type: 'manual',
        message: 'Välj facklig roll i ansökan',
      });
      return;
    }

    clearErrors('unionRole');

    try {
      onSubmittingChange?.(true);
      await submitContact({
        incidentId,
        unionRole,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
      }).unwrap();

      onSubmitted();
    } catch {
      setErrorMessage(
        texts?.existingBankruptcyContactErrorText || 'Ett fel uppstod'
      );
    } finally {
      onSubmittingChange?.(false);
    }
  };

  return (
    <>
      <TextBlock
        type="warning"
        text={texts?.existingBankruptcyWarningText ?? 'Varningstext saknas'}
      />
      <TextBlock
        type="info"
        text={texts?.existingBankruptcyContactIntroText ?? 'Info-text saknas'}
      />

      <UnionRoleSelect />

      <InputField
        name="companyContactFirstname"
        label={'Förnamn'}
        placeholder="Ange förnamn"
        required
      />
      <InputField
        name="companyContactLastname"
        label="Efternamn"
        placeholder="Ange efternamn"
        required
      />
      <InputField
        name="companyContactEmail"
        label="E-post"
        placeholder="exempel@mail.se"
        required
      />
      <InputField
        name="companyContactPhone"
        label="Telefon"
        placeholder="Ange telefonnummer"
        required
      />
      <CtaButtonWithIcon
        type="button"
        label={
          texts?.existingBankruptcyContactButtonText ?? 'Lägg till kontakt'
        }
        Icon={PersonAddIcon}
        disabled={isLoading}
        onClick={() => {
          void handleSubmitContact();
        }}
      />

      {errorMessage && <TextBlock type="error" text={errorMessage} />}
    </>
  );
};

export default ExistingBankruptcyContactBlock;
